import { computed, inject, signal } from "@angular/core";
import { Bind } from "../../../decorators/bind.decorator";
import { Action, S25ListDataComponent } from "../../../modules/s25-table-list/s25.list.data.component";
import { Item } from "../../../pojo/Item";
import { EventCopyOccurrencePayload } from "../../../pojo/ReservationI";
import { EventService } from "../../../services/event.service";
import { ListDataParams2 } from "../../../services/list.service";
import { UserprefService } from "../../../services/userpref.service";
import { FlsStore } from "../../../stores/fls.store";
import { S25Assert } from "../../../util/s25.assert";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { RelationshipMessages, RoseUtil } from "./rose.util.service";

/**
 * Button text and subtexts for copy/edit event modals and actions.
 * Shared between s25-more-actions-edit and s25-modal-copy-edit-event.
 */
export const ButtonLanguage = {
    clone: {
        onlyCurrent: {
            text: "Copy Only This Event",
            subText: "Create an independent copy of this event without relating it to any event sets.",
        },
        actionAndRelate: {
            text: "Copy and Relate",
            subText:
                "Create a copy of this event and relate it back to the original event or the original related event set.",
        },
        actionOnSelected: {
            text: "Copy and Create New Set",
            subText: "Create copies of each of the selected events and relate them in a new related event set.",
        },
    },
    edit: {
        onlyCurrent: {
            text: "Edit Only This Event",
            subText: "",
        },
        actionOnSelected: {
            text: "Edit Selected Events",
            subText: "",
        },
    },
} as const;

export interface EventCopyEditContext {
    eventId(): number | undefined;
    type(): ActionType | undefined;
    close?(): void;
    /**
     * Optional function to get mapped occurrence data for copy.
     * If not provided, Rose opens a plain clone without occurrence mapping.
     */
    getRoseOccurrencePayload?(itemIds?: number[]): EventCopyOccurrencePayload | undefined;
    /**
     * When true, clone actions are rendered outside the related-events list
     * (More Actions and Copy modal use a shared primary Copy button).
     */
    omitListCloneActions?: boolean;
    listComponent(): S25ListDataComponent | undefined;
}

export type ActionType = "edit" | "clone";

// TODO: STRONGLY consider converting this to a store
export class RoseEventCopyEditFeature {
    readonly fls = inject(FlsStore);

    readonly event = s25Resource({
        params: () => this.context.eventId(),
        loader: ({ params }) => EventService.getEventRelationships(params),
    });
    readonly allowedStates = s25Resource(() => UserprefService.getAllowedStates());
    readonly doClose = () => this.context.close?.();

    /* Selection tracking */
    readonly checkedItemsSet = signal<Set<number | string>>(new Set());
    readonly checkedItems = computed(() =>
        Array.from(this.checkedItemsSet())
            .map((id) => S25Util.toInt(id))
            .filter((id) => Number.isFinite(id)),
    );

    readonly relationships = computed(() => this.event.value()?.content ?? []);
    readonly canRelateEvents = computed(() => this.fls.hasFull("EVENT_ACTION"));
    readonly eventName = computed(() => {
        const ev = this.event.value();
        if (!ev) return "";
        return ev.event_name + (ev.event_locator ? ` (${ev.event_locator})` : "");
    });
    readonly headerMsg = computed(() => {
        const ev = this.event.value();
        if (!ev) return "";
        return ev.content ? RelationshipMessages.inSet : RelationshipMessages.notInSet;
    });
    readonly listQuery = computed<ListDataParams2 | undefined>(() => {
        const rels = this.relationships();
        if (!rels?.length) return undefined;

        const currentEventId = this.context.eventId();
        const relatedEventIds = rels
            .map((relationship) => relationship.content_event_id)
            .filter((eventId) => eventId !== currentEventId);
        const eventIdForList = currentEventId ? [currentEventId, ...relatedEventIds] : relatedEventIds;

        S25Assert.isDefined(this.context.type(), "Action type is required to compute allowed states");
        const states = RoseUtil.computeAllowedStates(
            this.context.type()!,
            this.allowedStates.value(),
            this.fls.value(),
        );

        return {
            itemType: "event" as Item.Subject,
            comptype: "event-quick-search",
            query: `&event_id=${eventIdForList.join("+")}&min_ols=C&state=${states.join("+")}`,
            itemsPerPage: 100,
        };
    });
    readonly checkListActions = computed((): Array<Action> => {
        if (this.context.type() === "clone") {
            if (this.context.omitListCloneActions) {
                return [];
            }
            return [
                {
                    type: "primary",
                    text: computed(() => ButtonLanguage.clone.onlyCurrent.text),
                    subText: computed(() => ButtonLanguage.clone.onlyCurrent.subText),
                    action: () => this.processCurrent(),
                    activateOnChecked: false,
                },
                ...(this.canRelateEvents()
                    ? ([
                          {
                              type: "primary",
                              text: computed(() => ButtonLanguage.clone.actionAndRelate.text),
                              subText: computed(() => ButtonLanguage.clone.actionAndRelate.subText),
                              action: () => this.copyAndRelateBack(),
                              activateOnChecked: false,
                          },
                          {
                              type: "primary",
                              text: computed(() => ButtonLanguage.clone.actionOnSelected.text),
                              subText: computed(() => ButtonLanguage.clone.actionOnSelected.subText),
                              action: () => this.copySelected(),
                              activateOnChecked: true,
                          },
                      ] as const)
                    : []),
            ];
        } else {
            return [
                {
                    type: "primary",
                    text: computed(() => ButtonLanguage.edit.onlyCurrent.text),
                    subText: computed(() => ButtonLanguage.edit.onlyCurrent.subText),
                    action: () => this.processCurrent(),
                    activateOnChecked: false,
                },
                {
                    type: "primary",
                    text: computed(() => ButtonLanguage.edit.actionOnSelected.text),
                    subText: computed(() => ButtonLanguage.edit.actionOnSelected.subText),
                    action: () => this.editSelected(),
                    activateOnChecked: true,
                },
            ];
        }
    });
    readonly Buttons = ButtonLanguage;

    constructor(private context: EventCopyEditContext) {}

    // The binds may not all be necessary, but people outside the feature
    // shouldn't have to care about which specific methods are bound.
    @Bind
    copyAndRelateBack(): Promise<void> {
        // Check if there are hidden private events
        const rels = this.relationships();
        const displayedRows = this.context.listComponent()?.tableComponent()?.rows;
        const totalEventIds = rels.length + 1; // relationships + current event

        if (displayedRows && totalEventIds > displayedRows.length) {
            // This is only true if there is some "(Private)" related event (not shown in our list rows)
            alert(RoseUtil.insufficientPrivilegesMsg);
            return Promise.resolve();
        }

        S25Assert.isDefined(this.context.eventId(), "Event ID is required to copy and relate back");
        return RoseUtil.copyAndRelateBack(
            this.context.eventId()!,
            this.context.getRoseOccurrencePayload?.(),
            this.doClose,
        );
    }

    @Bind
    processCurrent(): Promise<void> {
        S25Assert.isDefined(this.context.type(), "Action type is required to process current");
        S25Assert.isDefined(this.context.eventId(), "Event ID is required to process current");
        return RoseUtil.openEventInRose(
            this.context.type()!,
            this.context.eventId()!,
            this.doClose,
            this.context.getRoseOccurrencePayload?.(),
        );
    }

    @Bind
    copySelected(): Promise<void> {
        const itemIds = this.checkedItems();
        if (!itemIds.length) {
            return Promise.resolve();
        }
        S25Assert.isDefined(this.context.eventId(), "Event ID is required to copy selected");
        // Always request payload so related-only selections still receive dayOffset
        const occurrencePayload = this.context.getRoseOccurrencePayload?.(itemIds);
        return RoseUtil.copyAndRelateSelected(itemIds, occurrencePayload, this.doClose);
    }

    @Bind
    editSelected(): Promise<void> {
        const itemIds = this.checkedItems();
        if (!itemIds.length) {
            return Promise.resolve();
        }
        return RoseUtil.editSelected(itemIds, this.doClose);
    }

    @Bind
    handleSelectedChange(selected: Set<number | string>): void {
        this.checkedItemsSet.set(new Set(selected));
    }
}
